import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, IconButton, Button, CircularProgress, Snackbar, Alert,
  MenuItem, Select,
} from '@mui/material';
import { Delete as DeleteIcon, AddCircle as AddCircleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import api from '../../services/api';
import { getUser, isDemo } from '../../services/auth';
import { getDemoActivityTracker } from '../../services/demoActivityTracker';

interface InverterRow {
  id: number;
  nombre: string;
  ip_address: string;
  activo: boolean;
  mac?: string | null;
  datalogger_serial?: string;
}

interface SolarInverterIpsPanelProps {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  refreshKey?: number;
}

function isValidIP(ip: string): boolean {
  if (!ip || ip.trim() === '') return false;
  const parts = ip.trim().split('.');
  if (parts.length !== 4) return false;
  for (const part of parts) {
    const num = parseInt(part, 10);
    if (isNaN(num) || num < 0 || num > 255) return false;
  }
  return true;
}

const SolarInverterIpsPanel: React.FC<SolarInverterIpsPanelProps> = ({
  isDemoUser = false, onDemoActionAttempt, refreshKey = 0,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inverters, setInverters] = useState<InverterRow[]>([]);
  const [editedIPs, setEditedIPs] = useState<Record<number, string>>({});
  const [visibleInverterIds, setVisibleInverterIds] = useState<number[]>([]);
  const [adoptFrom, setAdoptFrom] = useState<Record<number, number>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const applyRows = useCallback((rows: InverterRow[], editing: boolean) => {
    const active = rows.filter((inv) => inv.activo === true);
    const initialIPs: Record<number, string> = {};
    let ids: number[] = [];
    if (active.length > 0) {
      active.forEach((inv) => {
        initialIPs[inv.id] = inv.ip_address || '';
        ids.push(inv.id);
      });
      ids.sort((a, b) => a - b);
    } else if (editing) {
      ids = [1];
      initialIPs[1] = '';
    } else {
      ids = [];
    }
    setEditedIPs(initialIPs);
    setVisibleInverterIds(ids);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/solar/inversores');
      const rows: InverterRow[] = Array.isArray(response.data) ? response.data : [];
      setInverters(rows);
      applyRows(rows, false);
      setIsEditing(false);
    } catch {
      setSnackbar({ open: true, message: t('configuration.errors.saveIPsError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [applyRows, t]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const notifyDemo = () => {
    onDemoActionAttempt?.();
    const user = getUser();
    if (user && isDemo(user)) {
      getDemoActivityTracker().trackClick('edit_inverter_ips', null, 'IP de inversores', {
        screen: 'settings',
        tab: 'solar',
        attemptedAction: 'edit_inverter_ips',
        blocked: true,
        blockReason: 'demo_user_no_configure',
      });
    }
    setSnackbar({ open: true, message: t('permissions.demoRestriction'), severity: 'error' });
  };

  const startEdit = () => {
    if (isDemoUser) {
      notifyDemo();
      return;
    }
    const active = inverters.filter((inv) => inv.activo === true);
    const initialIPs: Record<number, string> = {};
    let ids: number[] = [];
    if (active.length > 0) {
      active.forEach((inv) => {
        initialIPs[inv.id] = inv.ip_address || '';
        ids.push(inv.id);
      });
      ids.sort((a, b) => a - b);
    } else {
      ids = [1];
      initialIPs[1] = '';
    }
    setEditedIPs(initialIPs);
    setVisibleInverterIds(ids);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    applyRows(inverters, false);
    setIsEditing(false);
  };

  const handleIPChange = (inverterId: number, ip: string) => {
    if (isDemoUser) {
      notifyDemo();
      return;
    }
    setEditedIPs((prev) => ({ ...prev, [inverterId]: ip }));
  };

  const handleAddInverter = () => {
    if (isDemoUser) {
      notifyDemo();
      return;
    }
    const maxId = Math.max(...visibleInverterIds, 0);
    const newId = maxId + 1;
    setVisibleInverterIds((prev) => [...prev, newId]);
    setEditedIPs((prev) => ({ ...prev, [newId]: '' }));
  };

  const handleRetire = async (inv: InverterRow) => {
    if (isDemoUser) {
      notifyDemo();
      return;
    }
    const ok = window.confirm(t('configuration.inverterIPs.retireConfirm', {
      defaultValue: 'Se da de baja {{name}}. Deja de pedirse telemetría. El histórico se conserva.',
      name: inv.nombre,
    }));
    if (!ok) return;
    setSaving(true);
    try {
      await api.post(`/solar/inversores/${inv.id}/baja`);
      setSnackbar({
        open: true,
        message: t('configuration.inverterIPs.retireSuccess', { defaultValue: 'Inversor dado de baja. El histórico queda guardado.' }),
        severity: 'success',
      });
      await load();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || t('configuration.errors.saveIPsError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAdopt = async (retiredId: number) => {
    if (isDemoUser) {
      notifyDemo();
      return;
    }
    const fromId = adoptFrom[retiredId];
    if (!fromId) return;
    const retired = inverters.find((inv) => inv.id === retiredId);
    const source = inverters.find((inv) => inv.id === fromId);
    const ok = window.confirm(t('configuration.inverterIPs.adoptConfirm', {
      defaultValue: 'El stick de {{source}} pasa a {{target}}. El histórico sigue en el id {{id}}.',
      source: source?.nombre || String(fromId),
      target: retired?.nombre || String(retiredId),
      id: retiredId,
    }));
    if (!ok) return;
    setSaving(true);
    try {
      await api.post(`/solar/inversores/${retiredId}/adopt`, { from_id: fromId });
      setSnackbar({
        open: true,
        message: t('configuration.inverterIPs.adoptSuccess', { defaultValue: 'Stick reenganchado al mismo histórico.' }),
        severity: 'success',
      });
      await load();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || t('configuration.errors.saveIPsError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (isDemoUser) {
      notifyDemo();
      return;
    }
    const ipUpdates: Record<number, string> = {};
    for (const invId of visibleInverterIds) {
      const existingInv = inverters.find((inv) => inv.id === invId);
      const oldIP = existingInv?.ip_address || '';
      const newIP = editedIPs[invId] || '';
      if (oldIP !== newIP || (!existingInv && newIP.trim())) {
        ipUpdates[invId] = newIP.trim() || '0.0.0.0';
      }
    }
    if (Object.keys(ipUpdates).length === 0) {
      setSnackbar({ open: true, message: t('configuration.noChangesToSave'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      await api.put('/solar/inversores/ips', ipUpdates);
      setSnackbar({ open: true, message: t('configuration.inverterIPs.saveSuccess'), severity: 'success' });
      await load();
    } catch (ipError: any) {
      const errorData = ipError.response?.data;
      const currentLang = i18n.language || 'en';
      let ipErrorMessage = t('configuration.errors.saveIPsError');
      const detailStr = errorData?.detail
        ? (typeof errorData.detail === 'string' ? errorData.detail : String(errorData.detail))
        : '';
      if (detailStr.includes(' / ')) {
        const parts = detailStr.split(' / ');
        ipErrorMessage = currentLang.startsWith('en') && parts.length >= 2 ? parts[1].trim() : parts[0].trim();
      } else if (detailStr) {
        ipErrorMessage = detailStr;
      }
      setSnackbar({ open: true, message: ipErrorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const invertersToShow = visibleInverterIds.map((id) => {
    const existing = inverters.find((inv) => inv.id === id);
    if (existing) return existing;
    return {
      id,
      nombre: `Inversor ${id}`,
      ip_address: editedIPs[id] || '',
      activo: false,
    };
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#2391FF' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <Typography variant="subtitle1" sx={{ color: '#2391FF', marginBottom: 0.5, fontSize: '0.9rem', fontWeight: 'bold' }}>
        {t('configuration.inverterIPs.title')}
      </Typography>
      <Typography sx={{ color: '#999', fontSize: '0.75rem', marginBottom: 1, fontStyle: 'italic' }}>
        {t('configuration.inverterIPs.description')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        {!isEditing ? (
          <Button onClick={startEdit} variant="outlined" sx={{ color: '#2391FF', borderColor: '#2391FF', textTransform: 'none' }}>
            {t('common.edit', 'Editar')}
          </Button>
        ) : (
          <>
            <Button onClick={cancelEdit} disabled={saving} sx={{ color: '#bbb', textTransform: 'none' }}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving} variant="contained" sx={{ backgroundColor: '#2391FF', textTransform: 'none' }}>
              {saving ? t('common.saving', 'Guardando') : t('common.save', 'Guardar')}
            </Button>
          </>
        )}
      </Box>

      {invertersToShow.length === 0 && (
        <Typography sx={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>
          {t('configuration.inverterIPs.description')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {invertersToShow.map((inv, index) => {
          const currentIP = isEditing ? (editedIPs[inv.id] || '') : (inv.ip_address || '');
          const isActive = inv.activo && currentIP !== '0.0.0.0' && currentIP !== '';
          const hasValidIP = isValidIP(currentIP);
          const isLast = index === invertersToShow.length - 1;
          return (
            <Box key={inv.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingY: 0.3, gap: 1 }}>
              <Typography sx={{ color: '#999', fontSize: '0.8rem', minWidth: '100px' }}>
                {inv.nombre}
              </Typography>
              {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, maxWidth: '280px' }}>
                  <IconButton size="small" onClick={() => handleIPChange(inv.id, '')} disabled={isDemoUser} sx={{ color: '#ff4444', padding: '4px' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <TextField
                    type="text"
                    value={currentIP}
                    onChange={(e) => handleIPChange(inv.id, e.target.value)}
                    size="small"
                    disabled={isDemoUser}
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-input': { color: '#fff', fontSize: '0.8rem', padding: '3px 6px', height: '20px' },
                      '& .MuiOutlinedInput-root': {
                        height: '26px',
                        '& fieldset': { borderColor: '#666' },
                        '&:hover fieldset': { borderColor: '#999' },
                        '&.Mui-focused fieldset': { borderColor: '#2391FF' },
                      },
                    }}
                  />
                  {isLast && hasValidIP && (
                    <IconButton size="small" onClick={handleAddInverter} disabled={isDemoUser} sx={{ color: '#2391FF', padding: '4px' }}>
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1, maxWidth: '220px' }}>
                  <Typography sx={{ color: isActive ? '#00ff00' : '#ff4444', fontSize: '0.8rem', fontWeight: 'bold', flex: 1 }}>
                    {currentIP || '0.0.0.0'}
                  </Typography>
                  <Typography sx={{ color: isActive ? '#00ff00' : '#ff4444', fontSize: '0.7rem', fontStyle: 'italic' }}>
                    {isActive ? t('common.active', 'Activo') : t('common.inactive', 'Inactivo')}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => void handleRetire(inv)}
                    disabled={isDemoUser || saving}
                    sx={{ color: '#ff4444', padding: '4px' }}
                    aria-label={t('configuration.inverterIPs.retire', { defaultValue: 'Dar de baja' })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {!isEditing && inverters.some((inv) => !inv.activo) && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: '#2391FF', fontSize: '0.85rem', fontWeight: 'bold', mb: 0.5 }}>
            {t('configuration.inverterIPs.retiredTitle', { defaultValue: 'Dados de baja' })}
          </Typography>
          <Typography sx={{ color: '#999', fontSize: '0.72rem', mb: 1 }}>
            {t('configuration.inverterIPs.retiredHelp', {
              defaultValue: 'El histórico sigue en el mismo id. Si el stick nuevo tiene otra MAC o IP, reenganchalo acá.',
            })}
          </Typography>
          {inverters.filter((inv) => !inv.activo).map((inv) => {
            const sticks = inverters.filter((row) => row.activo);
            return (
              <Box key={`retired-${inv.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#ccc', fontSize: '0.8rem', minWidth: 120 }}>
                  {inv.nombre}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.72rem' }}>
                  {inv.ip_address || '—'}
                </Typography>
                {sticks.length > 0 && (
                  <>
                    <Select
                      size="small"
                      value={adoptFrom[inv.id] ? String(adoptFrom[inv.id]) : ''}
                      displayEmpty
                      onChange={(e) => setAdoptFrom((prev) => ({ ...prev, [inv.id]: Number(e.target.value) }))}
                      sx={{
                        minWidth: 140,
                        color: '#fff',
                        fontSize: '0.75rem',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                        '& .MuiSvgIcon-root': { color: '#aaa' },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('configuration.inverterIPs.adoptPick', { defaultValue: 'Stick nuevo' })}
                      </MenuItem>
                      {sticks.map((stick) => (
                        <MenuItem key={stick.id} value={String(stick.id)}>
                          {stick.nombre} · {stick.ip_address}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      size="small"
                      disabled={saving || !adoptFrom[inv.id]}
                      onClick={() => void handleAdopt(inv.id)}
                      sx={{ color: '#2391FF', textTransform: 'none' }}
                    >
                      {t('configuration.inverterIPs.adopt', { defaultValue: 'Reenganchar' })}
                    </Button>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SolarInverterIpsPanel;
